const normalizeId = (value) => (value == null ? "" : String(value).trim());

export const getDistinctRoomProposals = ({
  items = [],
  availableRoomsByReservation = {},
  preferredRoomByReservation = {},
  priorityReservationId = "",
} = {}) => {
  const entriesById = new Map();
  (Array.isArray(items) ? items : []).forEach((item) => {
    const reservationId = normalizeId(item?.reservationId);
    if (!reservationId || entriesById.has(reservationId)) return;
    const rooms = availableRoomsByReservation?.[reservationId];
    const roomIds = [...new Set(
      (Array.isArray(rooms) ? rooms : [])
        .map((room) => normalizeId(room?.id ?? room?.room_id))
        .filter(Boolean)
    )];
    const preferredRoomId = normalizeId(preferredRoomByReservation?.[reservationId]);
    const validPreference = roomIds.includes(preferredRoomId) ? preferredRoomId : "";
    entriesById.set(reservationId, {
      reservationId,
      preferredRoomId: validPreference,
      roomIds: validPreference
        ? [validPreference, ...roomIds.filter((roomId) => roomId !== validPreference)]
        : roomIds,
    });
  });

  const entries = [...entriesById.values()];
  const proposals = Object.fromEntries(entries.map(({ reservationId }) => [reservationId, ""]));
  const ownerByRoomId = new Map();
  const priorityEntry = entriesById.get(normalizeId(priorityReservationId));
  const lockedReservationId = priorityEntry?.preferredRoomId ? priorityEntry.reservationId : "";

  const assign = (reservationId, roomId) => {
    proposals[reservationId] = roomId;
    ownerByRoomId.set(roomId, reservationId);
  };

  // Keep the latest explicit selection fixed while coordinating the other rows.
  if (lockedReservationId) assign(lockedReservationId, priorityEntry.preferredRoomId);
  entries.forEach(({ reservationId, preferredRoomId }) => {
    if (preferredRoomId && !ownerByRoomId.has(preferredRoomId)) {
      assign(reservationId, preferredRoomId);
    }
  });

  const findRoom = (entry, visitedRooms) => {
    // Use a free room before moving another row's still-valid proposal.
    const freeRoomId = entry.roomIds.find((roomId) => !ownerByRoomId.has(roomId));
    if (freeRoomId) {
      assign(entry.reservationId, freeRoomId);
      return true;
    }

    for (const roomId of entry.roomIds) {
      if (visitedRooms.has(roomId)) continue;
      visitedRooms.add(roomId);
      const ownerId = ownerByRoomId.get(roomId);
      if (ownerId === lockedReservationId) continue;
      if (findRoom(entriesById.get(ownerId), visitedRooms)) {
        assign(entry.reservationId, roomId);
        return true;
      }
    }
    return false;
  };

  // Augmenting paths avoid leaving a row empty when reassignment can fit it.
  entries.forEach((entry) => {
    if (!proposals[entry.reservationId]) findRoom(entry, new Set());
  });
  return proposals;
};
